import React, { useEffect, useMemo, Fragment, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import echo from '../services/echo';
import { Toaster, toast } from 'react-hot-toast';
import { Dialog, Transition } from '@headlessui/react';
import {
  HomeIcon,
  WrenchScrewdriverIcon,
  ArrowLeftOnRectangleIcon,
  ChartBarIcon,
  QrCodeIcon,
  UsersIcon,
  TagIcon,
  BellAlertIcon,
  Bars3Icon,
  XMarkIcon,
  ArchiveBoxIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentCheckIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';


interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  roles: string[]; // Define quais perfis podem ver este link
}

const MainLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Lógica de Navegação refatorada para ser mais clara e segura.
  // `useMemo` otimiza o desempenho, recalculando a lista apenas quando o `user` muda.
  const navigation = useMemo(() => {
    // 1. Define todos os links de navegação possíveis e os perfis que os podem ver.
    const allNavItems: NavItem[] = [
      { name: 'Dashboard', href: '/', icon: HomeIcon, roles: ['Administrador', 'Supervisor', 'Técnico', 'Coordenador', 'Gerente'] },
      { name: 'Alertas', href: '/maintenance-alerts', icon: BellAlertIcon, roles: ['Administrador', 'Supervisor', 'Coordenador', 'Gerente'] },
      { name: 'Ferramentas', href: '/tools', icon: WrenchScrewdriverIcon, roles: ['Administrador', 'Supervisor', 'Técnico', 'Coordenador', 'Gerente'] },
      { name: 'Kits', href: '/kits', icon: ArchiveBoxIcon, roles: ['Administrador', 'Supervisor', 'Coordenador', 'Gerente'] },
      { name: 'Termos', href: '/terms', icon: ClipboardDocumentCheckIcon, roles: ['Administrador', 'Supervisor', 'Técnico', 'Coordenador', 'Gerente'] },
      { name: 'Relatórios', href: '/reports', icon: ChartBarIcon, roles: ['Administrador', 'Supervisor', 'Coordenador', 'Gerente'] },
      { name: 'Usuários', href: '/users', icon: UsersIcon, roles: ['Administrador', 'Supervisor', 'Coordenador', 'Gerente'] },
      { name: 'Categorias', href: '/categories', icon: TagIcon, roles: ['Administrador'] },
      { name: 'Centros de Custo', href: '/cost-centers', icon: BuildingOffice2Icon, roles: ['Administrador']},
      { name: 'Importar/Exportar', href: '/import', icon: ArrowUpTrayIcon, roles: ['Administrador'] },
    ];

    // 2. Garante que o utilizador e o seu perfil já foram carregados.
    if (!user?.role?.name) {
      return []; // Retorna um menu vazio enquanto os dados do utilizador carregam.
    }

    // 3. Filtra a lista completa, mantendo apenas os itens que o perfil do utilizador atual pode ver.
    return allNavItems.filter(item => item.roles.includes(user.role.name));
  }, [user]);

  // Efeito para se conectar ao canal de notificações do Pusher.
  useEffect(() => {
    if (user) {
      echo.private(`user.${user.id}`)
        .listen('.tool.assigned', (data: any) => {
          toast.success(data.message, {
            duration: 6000,
            icon: '✅',
          });
        });
    }
    return () => {
      if (user) {
        echo.leave(`user.${user.id}`);
      }
    };
  }, [user]);

  const sidebarContent = (
    <>
      <div className="h-16 flex items-center justify-center px-4 bg-gray-900">
        <h1 className="text-xl font-bold text-white">GestãoFerramentas PRO</h1>
      </div>
      <nav className="flex-1 px-2 py-4 space-y-1">
        {navigation.map((item) => (
          <Link
            key={item.name}
            to={item.href}
            onClick={() => setSidebarOpen(false)}
            className={`
              ${location.pathname === item.href ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}
              group flex items-center px-2 py-2 text-sm font-medium rounded-md
            `}
          >
            <item.icon className="mr-3 h-6 w-6 flex-shrink-0" />
            {item.name}
          </Link>
        ))}
      </nav>
      <div className="p-4 border-t border-gray-700">
        <Link to="/profile" onClick={() => setSidebarOpen(false)} className="block hover:bg-gray-700 rounded-md p-2">
          <div className="text-sm">
            <p className="font-semibold text-white">{user?.name}</p>
            <p className="text-gray-400">{user?.role?.name}</p>
          </div>
        </Link>
        <button
          onClick={logout}
          className="mt-2 w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-300 hover:bg-gray-700 hover:text-white"
        >
          <ArrowLeftOnRectangleIcon className="mr-3 h-6 w-6" />
          Sair
        </button>
      </div>
    </>
  );

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      <Toaster position="top-right" />
      {/* Sidebar para ecrãs móveis (overlay) */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>
          <div className="fixed inset-0 flex z-40">
            <Transition.Child as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="-translate-x-full" enterTo="translate-x-0" leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="-translate-x-full">
              <Dialog.Panel className="relative flex-1 flex flex-col max-w-xs w-full bg-gray-800">
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button type="button" className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" onClick={() => setSidebarOpen(false)}>
                    <XMarkIcon className="h-6 w-6 text-white" />
                  </button>
                </div>
                {sidebarContent}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
      {/* Sidebar estática para ecrãs grandes */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 bg-gray-800">{sidebarContent}</div>
        </div>
      </div>
      {/* Conteúdo Principal */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="lg:hidden pl-1 pt-1 sm:pl-3 sm:pt-3">
          <button type="button" className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none" onClick={() => setSidebarOpen(true)}>
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>
        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none">
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;